import React, { type PropsWithChildren, useState } from "react";
import { Box, Flex, SimpleGrid } from "@chakra-ui/react";
import { Section } from "components/section";
import { TextTitle } from "ui/text";
import Balancer from "react-wrap-balancer";
import { type BrandColorVariants } from "theme/colors";
import { FillImage } from "components/images";
import agentsUi from "../../../../public/assets/home/agents-ui.png";
import { PlatformCard, type PlatformCardVariant, PlatformVariants } from "./platform-card";
import { AidMedia } from "./aid-media";
import { CdpMedia } from "./cdp-media";

export function PlatformCards() {
  const [activeCard, setActiveCard] = useState<PlatformCardVariant | undefined>(undefined);

  function handleCardMouseEnter(cardVariant: PlatformCardVariant) {
    setActiveCard(cardVariant);
  }

  function handleCardMouseLeave() {
    setActiveCard(undefined);
  }

  return (
    <Section>
      <TextTitle
        align="center"
        mobileAlign="center"
        size="lg"
        maxW="100%"
        title={
          <Balancer>A complete platform to make your marketers faster, more strategic, and more creative</Balancer>
        }
      />

      <Flex flexDir="column" gap="big" mt={{ base: "bigger", md: "biggest" }}>
        <PlatformCard
          isHovered={activeCard === PlatformVariants.AGENTS}
          variant={PlatformVariants.AGENTS}
          eyebrow="Hightouch Agents"
          title="AI that understands your brand and helps marketers move 10x faster"
          description="Answer questions, uncover opportunities, and automate workflows with purpose-built marketing AI"
          href="/platform/agents"
          ctaText="Learn about Hightouch Agents"
          media={<AgentsUI isActive={activeCard === PlatformVariants.AGENTS} />}
          onMouseEnter={() => {
            handleCardMouseEnter(PlatformVariants.AGENTS);
          }}
          onMouseLeave={() => {
            handleCardMouseLeave();
          }}
        />

        <SimpleGrid justifyItems={{ base: "center", lg: "stretch" }} columns={{ base: 1, lg: 2 }} gap="small">
          <PlatformCard
            isHovered={activeCard === PlatformVariants.CDP}
            variant={PlatformVariants.CDP}
            eyebrow="Composable CDP"
            title="Finally, a CDP that actually works"
            description="A Composable CDP is built on top of your data warehouse. It’s flexible, secure, quick-to-deploy, and empowers marketers with self-service data."
            href="/platform/composable-cdp"
            ctaText="Learn about Composable CDP"
            media={
              <IconBox isActive={activeCard === PlatformVariants.CDP} color="green">
                <CdpMedia isActive={activeCard === PlatformVariants.CDP} />
              </IconBox>
            }
            onMouseEnter={() => {
              handleCardMouseEnter(PlatformVariants.CDP);
            }}
            onMouseLeave={() => {
              handleCardMouseLeave();
            }}
          />
          <PlatformCard
            isHovered={activeCard === PlatformVariants.AID}
            variant={PlatformVariants.AID}
            eyebrow="AI Decisioning"
            title="1:1 decisioning for lifecycle marketing"
            description="AI Decisioning delivers a unique and optimized experience for each of your customers across channels at massive scale."
            href="/platform/ai-decisioning"
            ctaText="Learn about AI Decisioning"
            media={
              <IconBox isActive={activeCard === PlatformVariants.AID} color="violet">
                <AidMedia isActive={activeCard === PlatformVariants.AID} />
              </IconBox>
            }
            onMouseEnter={() => {
              handleCardMouseEnter(PlatformVariants.AID);
            }}
            onMouseLeave={() => {
              handleCardMouseLeave();
            }}
          />
        </SimpleGrid>
      </Flex>
    </Section>
  );
}

function IconBox({
  isActive,
  color,
  children,
}: PropsWithChildren<{ isActive: boolean; color: keyof BrandColorVariants }>) {
  return (
    <Flex
      aria-hidden="true"
      display={{ base: "none", md: "flex" }}
      align="center"
      justify="center"
      border="6px solid white"
      borderRadius={{ base: "small", xl: "big" }}
      boxSize={{ base: "80px", xl: "120px" }}
      bg={`new.${color}.50`}
      m={{ base: "biggest", lg: "bigger", xl: "biggest" }}
      ml={{ base: "0", lg: "0", xl: "0" }}
      shadow="card"
      transform={isActive ? "scale(1.03)" : "scale(1)"}
      transition="transform 0.25s ease-out"
    >
      {children}
    </Flex>
  );
}

function AgentsUI({ isActive }: { isActive: boolean }) {
  return (
    <Box
      pos="absolute"
      top={0}
      left={{ base: "50%", lg: "50%" }}
      width="920px"
      height="568px"
      transform={isActive ? "scale(1.015)" : "scale(1)"}
      transition="transform 0.25s ease-out"
    >
      <Box pos="relative" boxSize="100%">
        <FillImage src={agentsUi} alt="Agents UI" placeholder="empty" />
      </Box>
    </Box>
  );
}
